import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, FormControl, FormLabel, Text, useToast } from '@chakra-ui/react';
import { getUniqueFileName, readFileAsBase64 } from '@netiva/classifieds-common';
import { FileInput, FileInputClickBox, Loader } from '@netiva/classifieds-ui';
import { v4 as uuid } from 'uuid';

import { DocumentTypes, VerificationDocumentTypes } from '@/lib/fileTypes';
import { getIdOrFileName } from '@/lib/utils';
import { useAppDispatch, useAppSelector } from '@/store';
import { adActions } from '@/store/ad';
import { AdFile } from '@/store/ad/types';
import { useAd } from '@/pages/Ad/hooks';
import { UploadedFile } from '@/pages/Ad/providers';
import { AdComponentProps } from '@/pages/Ad/types';
import { DocumentListItem } from './components';

import { DocumentUploadLimit, VerificationDocumentUploadLimit } from '@/environment';
import { useMatomoTagManager } from '@/hooks';

export type DocumentSelectorProps = AdComponentProps & {
  /** Verification documents are only shown to the backoffice, never on the public ad. */
  variant?: 'public' | 'verification';
};

export const DocumentSelector: FC<DocumentSelectorProps> = ({ trackingAction, variant = 'public' }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const { images, documents: publicDocuments, verificationDocuments } = useAppSelector((state) => state.ad);
  const { uploadedFiles, setUploadedFiles } = useAd();
  const [isLoading, setIsLoading] = useState(false);
  const [replacingDocument, setReplacingDocument] = useState<AdFile>();

  const { trackMatomoEvent } = useMatomoTagManager();

  const isVerification = variant === 'verification';
  const documents = isVerification ? verificationDocuments : publicDocuments;
  const hasDocument = documents.length > 0;
  const uploadLimit = isVerification ? VerificationDocumentUploadLimit : DocumentUploadLimit;
  const acceptedTypes = isVerification ? VerificationDocumentTypes : DocumentTypes;

  const addDocument = (document: AdFile) =>
    dispatch(isVerification ? adActions.addVerificationDocument(document) : adActions.addDocument(document));
  const replaceDocument = (oldDocument: AdFile, newDocument: AdFile) =>
    dispatch(
      isVerification
        ? adActions.replaceVerificationDocument({ oldDocument, newDocument })
        : adActions.replaceDocument({ oldDocument, newDocument })
    );
  const removeDocument = (document: AdFile) =>
    dispatch(isVerification ? adActions.removeVerificationDocument(document) : adActions.removeDocument(document));

  // All uploads share one multipart field and the API assigns them to their list by file name.
  const getTakenFileNames = (replacedFileName?: string) =>
    [...images, ...publicDocuments, ...verificationDocuments, ...uploadedFiles]
      .map((f) => f.fileName)
      .filter((name): name is string => !!name && name !== replacedFileName);

  const showInvalidFileNotification = (name: string) => {
    toast({
      status: 'error',
      title: t('ad.steps.files.documents.invalidFile.title'),
      description: t('ad.steps.files.documents.invalidFile.description', { name }),
      duration: 10000,
      isClosable: true,
    });
  };

  const showFileLimitReachedNotification = (limit: number) => {
    toast({
      status: 'error',
      title: t('ad.steps.files.documents.fileLimit.title'),
      description: t('ad.steps.files.documents.fileLimit.description', { limit }),
      duration: 10000,
      isClosable: true,
    });
  };

  const handleFilesSelected = async (files: FileList) => {
    setIsLoading(true);

    if (!replacingDocument && documents.length >= uploadLimit) {
      showFileLimitReachedNotification(uploadLimit);
      setIsLoading(false);
      return;
    }

    // the store is not updated until the next render, so names given out in this loop are tracked here
    const takenFileNames = getTakenFileNames(replacingDocument?.fileName);
    for (const file of files) {
      try {
        const data = await readFileAsBase64(file);
        if (data?.contentType && acceptedTypes.includes(data.contentType)) {
          const fileName = getUniqueFileName(file.name || uuid(), takenFileNames);
          takenFileNames.push(fileName);
          const newDocument: AdFile = {
            id: 0,
            fileName,
          };
          const newFile: UploadedFile = {
            fileName,
            file: fileName === file.name ? file : new File([file], fileName, { type: file.type }),
          };
          if (replacingDocument) {
            setUploadedFiles((prev) => prev.filter((x) => x.fileName !== replacingDocument.fileName).concat(newFile));
            replaceDocument(replacingDocument, newDocument);
          } else {
            trackMatomoEvent({
              trackingCategory: 'Interaction_PI-Insertion',
              trackingAction: `${trackingAction}_Upload_${data.contentType === 'application/pdf' ? 'PDF' : 'Image'}`,
            });
            setUploadedFiles((prev) => [...prev, newFile]);
            addDocument(newDocument);
          }
        } else {
          showInvalidFileNotification(file.name);
        }
      } catch (err) {
        console.error('Failed to read file', file, err);
        showInvalidFileNotification(file.name);
      } finally {
        setReplacingDocument(undefined);
      }
    }
    setIsLoading(false);
  };

  const handleRemove = (e: React.MouseEvent, file: AdFile) => {
    e.preventDefault();
    e.stopPropagation();
    removeDocument(file);
  };
  const handleReplace = (_: React.MouseEvent, image: AdFile) => {
    setReplacingDocument(image);
  };

  return (
    <FormControl my={2} position="relative">
      <Loader isLoading={isLoading} />
      <FormLabel>
        {isVerification
          ? t('ad.steps.verificationDocuments.label', { limit: uploadLimit })
          : t('ad.steps.files.documents.label', { count: DocumentUploadLimit > 1 ? 2 : 1, limit: DocumentUploadLimit })}
      </FormLabel>
      <FileInput onFilesSelected={handleFilesSelected} accept={acceptedTypes.join(',')}>
        {!hasDocument && <Text>{t('ad.steps.files.documents.drag')}</Text>}
        {hasDocument && (
          <Flex flexWrap="wrap" justify="center" align="stretch" gap={4} mb={4}>
            {documents.map((doc) => (
              <DocumentListItem
                key={getIdOrFileName(doc)}
                document={doc}
                onRemove={(e) => handleRemove(e, doc)}
                onReplace={(e) => handleReplace(e, doc)}
              />
            ))}
          </Flex>
        )}
        <Flex wrap="wrap" gap={2}>
          <FileInputClickBox>
            <Button type="button" size="sm" colorScheme="secondary">
              {t('ad.steps.files.documents.upload')}
            </Button>
          </FileInputClickBox>
        </Flex>
      </FileInput>
    </FormControl>
  );
};
